import type { TmsFactoringResult, TmsInvoiceCreateRow, TmsInvoiceRow } from '@numeo/types-main-web-app'
import { approveWrite as approveWriteCard, type WriteOutcome } from '../../api/writeCardApi'
import type { ChatNoteAction } from '../../types'
import { formatUsd } from '../results/format'
import type { ApproveWrite } from './sendBatch'
import { eligibleOf, rowKeyOf } from './invoicingRows'
import { FACTORING_BATCH_MIN } from '../results/refusalWords'

/** The most invoices one factoring batch takes (the card's own limit). */
export const BATCH_SIZE = 20

/** What one batch did: its invoices, and the card's words for it; a prepared one also its name, count, total and next step. */
interface BatchOutcome {
    invoiceNumbers: string[]
    prepared: boolean
    message: string
    /** The batch the card made (its answer's `batch_name`), when prepared. */
    batchName?: string
    /** The factor's display name the card named. */
    provider?: string
    /** How many invoices the batch took, and their total (the card's own rows). */
    invoiceCount?: number
    totalUsd?: number
    /** Whether the batch may be submitted from the chat (`tms_factoring.submit_batch`), else from the Invoices page. */
    submitInChat?: boolean
}

/** A prepare answer as an outcome: prepared only when the card answered `updated` (a refusal is an answer too, with `outcome: refused`). */
const preparedOutcomeOf = (invoiceNumbers: string[], answer: WriteOutcome): BatchOutcome => {
    const result = answer.structured as Partial<TmsFactoringResult>
    const prepared = answer.status === 'answered' && (result.outcome === 'updated' || result.outcome === 'partial') && !!result.batch_name
    if (!prepared) return { invoiceNumbers, prepared: false, message: answer.text }
    const rows = (result.rows ?? []).filter((row) => row.outcome === 'batched')
    const total = rows.reduce((sum, row) => sum + (row.amount_usd ?? 0), 0)
    return {
        invoiceNumbers,
        prepared: true,
        message: answer.text,
        batchName: result.batch_name ?? undefined,
        provider: result.provider_label ?? undefined,
        invoiceCount: rows.length || invoiceNumbers.length,
        totalUsd: Math.round(total * 100) / 100,
        submitInChat: result.submit_in_chat === true,
    }
}

/**
 * Splits the eligible rows into batches of at most `BATCH_SIZE`, in order. A last batch smaller than the minimum takes rows from the one
 * before it (21 rows are 19 + 2, never 20 + 1), so no batch is refused for its size.
 */
export const batchesOf = (rows: TmsInvoiceRow[]): TmsInvoiceRow[][] => {
    const batches: TmsInvoiceRow[][] = []
    for (let index = 0; index < rows.length; index += BATCH_SIZE) batches.push(rows.slice(index, index + BATCH_SIZE))
    const last = batches[batches.length - 1]
    if (batches.length > 1 && last.length < FACTORING_BATCH_MIN) {
        const before = batches[batches.length - 2]
        const moved = before.splice(before.length - (FACTORING_BATCH_MIN - last.length))
        batches[batches.length - 1] = [...moved, ...last]
    }
    return batches
}

/** What a factoring batch could take from these rows: the eligible ones, and whether they are enough for a batch. */
interface FactoringEligibility {
    eligible: TmsInvoiceRow[]
    /** At least the minimum a new batch takes (`FACTORING_BATCH_MIN`). */
    batchable: boolean
}

/**
 * The one eligibility rule of every factoring offer and of the batch drawer: the rows that can be batched (`eligibleOf`: ready loads and
 * drafts, never a sent, batched or issue-blocked one), and whether there are enough of them for a batch.
 */
export const factoringEligibilityOf = (rows: TmsInvoiceRow[]): FactoringEligibility => {
    const eligible = eligibleOf(rows)
    return { eligible, batchable: eligible.length >= FACTORING_BATCH_MIN }
}

/**
 * Prepares the eligible loads as factoring batches of up to 20, the drawer's button answering each batch's question (the caller must
 * be the factoring drawer, whose list is what the user approved). A load with no invoice gets its single-load invoice first. Nothing is
 * sent to the factor here: submitting stays the chat's approval card. A failed call is reported for its batch and not retried.
 */
export async function prepareFactoringBatches(tabId: string, rows: TmsInvoiceRow[], approveWrite: ApproveWrite = approveWriteCard): Promise<BatchOutcome[]> {
    const outcomes: BatchOutcome[] = []
    for (const batch of batchesOf(rows)) {
        const numbers: string[] = []
        try {
            for (const row of batch) {
                if (row.invoice_number) {
                    numbers.push(row.invoice_number)
                    continue
                }
                const created = await approveWrite(tabId, 'tms_invoices.create', { load_numbers: [row.load_numbers[0] ?? rowKeyOf(row)] })
                const line = created.structured.rows?.find((r) => r.load_number === row.load_numbers[0]) as TmsInvoiceCreateRow | undefined
                if (!line?.invoice_number) {
                    outcomes.push({ invoiceNumbers: [...numbers], prepared: false, message: line?.message ?? created.text })
                    numbers.length = 0
                    break
                }
                numbers.push(line.invoice_number)
            }
            if (numbers.length !== batch.length) continue
            // The drawer's "Approve & prepare batch" listed these invoices: that click is the batch's approval (nothing goes to the factor).
            const prepared: WriteOutcome = await approveWrite(tabId, 'tms_factoring.prepare_batch', { invoice_numbers: numbers }, { approvedInDrawer: true })
            outcomes.push(preparedOutcomeOf([...numbers], prepared))
        } catch {
            outcomes.push({ invoiceNumbers: [...numbers], prepared: false, message: 'Not prepared. The request failed, so nothing was retried.' })
        }
    }
    return outcomes
}

/**
 * The agent message a prepared batch adds to the chat (v17): "Prepared BATCH-… for RTS Pro: 2 invoices, $1,800.00. It is not sent to the
 * factor yet." With submitting from the chat on, its next step asks the agent to submit the batch: that is the user's chat message, and
 * `tms_factoring.submit_batch` asks its own approval as always (it is never answered from here). Off, it says where to submit it.
 */
export const preparedNoteOf = (outcome: BatchOutcome, fallbackProvider: string): [string, ChatNoteAction?] => {
    const provider = outcome.provider || fallbackProvider || 'the factor'
    const count = outcome.invoiceCount ?? outcome.invoiceNumbers.length
    const text = `Prepared ${outcome.batchName} for ${provider}: ${count} invoice${count === 1 ? '' : 's'}, ${formatUsd(outcome.totalUsd ?? 0)}. It is not sent to the factor yet.`
    if (!outcome.submitInChat) return [`${text} Open Accounting → Factoring to submit it.`]
    return [text, { label: `Submit to ${provider}`, message: `Submit factoring batch ${outcome.batchName} to ${provider}.` }]
}

/** What the factoring offer stands for: the rows a batch would take (the table's, then the company's others) and their whole count. */
interface FactoringOffer {
    rows: TmsInvoiceRow[]
    count: number
}

/**
 * The factoring offer over the company, not only the table (S17): the table's eligible rows, then each batchable invoice the server read
 * (`company`) that the table does not list and that is still eligible as the store shows it, and the invoices past the read's 50 by
 * count. With no company read yet, the table's rows alone.
 */
export const factoringOfferOf = (tableEligible: TmsInvoiceRow[], company: { rows: TmsInvoiceRow[]; total: number | null }): FactoringOffer => {
    if (company.total === null) return { rows: tableEligible, count: tableEligible.length }
    const listed = new Set(tableEligible.flatMap((row) => [rowKeyOf(row), ...(row.invoice_number ? [`#${row.invoice_number}`] : [])]))
    const others = eligibleOf(company.rows).filter((row) => !listed.has(rowKeyOf(row)) && !(row.invoice_number && listed.has(`#${row.invoice_number}`)))
    const rows = [...tableEligible, ...others]
    return { rows, count: rows.length + Math.max(0, company.total - company.rows.length) }
}
